import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { api } from '@/services/api';
import {
  getAuthObject,
  setAuthObject,
  removeAuthObject,
  getSelectedCompanyInfo,
  setSelectedCompanyInfo,
  removeSelectedCompanyInfo,
  getCurrentAccessToken,
} from '@/utils/auth';
import { hasValue, hasStringValue } from '@/utils/validation';
import { notifyApp } from '@/services/app-notify';
import { showToast } from '@/contexts/ToastContext';
import { isIOSApp, isAndroidApp } from '@/utils/browser';

interface NpsdClientInfo {
  npsdClientId: number;
  name: string;
  hasActivityReportAccess: boolean;
  hasCaseReportAccess: boolean;
  hasCallCardAccess: boolean;
  hasAnalyticsReportAccess: boolean;
}

interface ClientListItem {
  id: number | string | null;
  name: string;
}

interface AuthContextValue {
  authLoading: boolean;
  isAuthenticated: boolean;
  companyList: any[];
  selectedCompanyId: number | null;
  selectedCompanyName: string;
  npsdClientInfoList: NpsdClientInfo[];
  activityReportNpsdClientList: ClientListItem[];
  caseReportNpsdClientList: ClientListItem[];
  callsForServiceNpsdClientList: ClientListItem[];
  analyticsReportNpsdClientList: ClientListItem[];
  companyLogoUrl: string | null;
  isCompanyNameOnNav: boolean;
  isCompanyLogoOnNav: boolean;
  signIn: (result: any) => Promise<'home' | 'companySelection'>;
  selectCompany: (company: { id: number; name: string }) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [companyList, setCompanyList] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [npsdClientInfoList, setNpsdClientInfoList] = useState<NpsdClientInfo[]>([]);
  const [activityReportNpsdClientList, setActivityReportNpsdClientList] = useState<ClientListItem[]>([]);
  const [caseReportNpsdClientList, setCaseReportNpsdClientList] = useState<ClientListItem[]>([]);
  const [callsForServiceNpsdClientList, setCallsForServiceNpsdClientList] = useState<ClientListItem[]>([]);
  const [analyticsReportNpsdClientList, setAnalyticsReportNpsdClientList] = useState<ClientListItem[]>([]);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [isCompanyNameOnNav, setIsCompanyNameOnNav] = useState(false);
  const [isCompanyLogoOnNav, setIsCompanyLogoOnNav] = useState(false);

  const buildClientLists = useCallback((clientInfoList: NpsdClientInfo[]) => {
    const activityList: ClientListItem[] = [{ id: null, name: 'Search All' }];
    const caseList: ClientListItem[] = [{ id: '', name: 'Search All' }];
    const cfsList: ClientListItem[] = [{ id: '', name: 'Search All' }];
    const analyticsList: ClientListItem[] = [];

    for (const info of clientInfoList) {
      if (info.hasActivityReportAccess) {
        activityList.push({ id: info.npsdClientId, name: info.name });
      }
      if (info.hasCaseReportAccess) {
        caseList.push({ id: info.npsdClientId, name: info.name });
      }
      if (info.hasCallCardAccess) {
        cfsList.push({ id: info.npsdClientId, name: info.name });
      }
      if (info.hasAnalyticsReportAccess) {
        analyticsList.push({ id: info.npsdClientId, name: info.name });
      }
    }

    setActivityReportNpsdClientList(activityList);
    setCaseReportNpsdClientList(caseList);
    setCallsForServiceNpsdClientList(cfsList);
    setAnalyticsReportNpsdClientList(analyticsList);
  }, []);

  const selectCompany = useCallback(async (company: { id: number; name: string }) => {
    try {
      const result = await api.selectCompany(company.id);
      const clientInfoList = result.npsdClientRowList;
      setNpsdClientInfoList(clientInfoList);
      setCompanyLogoUrl(result.companyLogo);
      setIsCompanyNameOnNav(result.isCompanyNameOnNav);
      setIsCompanyLogoOnNav(result.isCompanyLogoOnNav);
      buildClientLists(clientInfoList);
      setSelectedCompanyId(company.id);
      setSelectedCompanyName(company.name);
      setIsAuthenticated(true);
      setSelectedCompanyInfo({
        company,
        npsdClientInfoList: clientInfoList,
        companyLogoUrl: result.companyLogo,
        isCompanyNameOnNav: result.isCompanyNameOnNav,
        isCompanyLogoOnNav: result.isCompanyLogoOnNav,
      });
    } catch {
      showToast('Error selecting company.', 'error');
      throw new Error('Failed to select company');
    }
  }, [buildClientLists]);

  const signIn = useCallback(async (result: any): Promise<'home' | 'companySelection'> => {
    if (hasValue(result.access_token)) {
      setAuthObject(result);
    } else {
      removeAuthObject();
    }

    if (isIOSApp) {
      notifyApp('login', [getCurrentAccessToken()!, api.iosDeviceTokenEndpoint!]);
    } else if (isAndroidApp) {
      notifyApp('login', [getCurrentAccessToken()!, api.androidDeviceTokenEndpoint!]);
    } else {
      notifyApp('login', [getCurrentAccessToken()!]);
    }

    if (!hasValue(result.companyList) || result.companyList.length === 0) {
      showToast('Error: Please contact your administrator for access.', 'error');
      throw new Error('No companies available');
    }

    setCompanyList(result.companyList);

    if (result.companyList.length === 1) {
      await selectCompany(result.companyList[0]);
      return 'home';
    }

    return 'companySelection';
  }, [selectCompany]);

  const signOut = useCallback(() => {
    api.logout().catch(() => {});
    removeAuthObject();
    removeSelectedCompanyInfo();
    notifyApp('logout');
    setIsAuthenticated(false);
    setCompanyList([]);
    setSelectedCompanyId(null);
    setSelectedCompanyName('');
    setNpsdClientInfoList([]);
    setActivityReportNpsdClientList([]);
    setCaseReportNpsdClientList([]);
    setCallsForServiceNpsdClientList([]);
    setAnalyticsReportNpsdClientList([]);
    setCompanyLogoUrl(null);
    setIsCompanyNameOnNav(false);
    setIsCompanyLogoOnNav(false);
  }, []);

  // Restore from localStorage on mount
  useEffect(() => {
    const authObj = getAuthObject();
    const selectedCompanyInfo = getSelectedCompanyInfo();
    if (hasValue(authObj) && hasValue(selectedCompanyInfo)) {
      setCompanyList(authObj.companyList || []);
      const company = selectedCompanyInfo.company;
      const clientInfoList = selectedCompanyInfo.npsdClientInfoList;
      setSelectedCompanyId(company.id);
      setSelectedCompanyName(company.name);
      setNpsdClientInfoList(clientInfoList);
      setCompanyLogoUrl(selectedCompanyInfo.companyLogoUrl);
      setIsCompanyNameOnNav(selectedCompanyInfo.isCompanyNameOnNav);
      setIsCompanyLogoOnNav(selectedCompanyInfo.isCompanyLogoOnNav);
      buildClientLists(clientInfoList);
      setIsAuthenticated(true);
    }
    setAuthLoading(false);
  }, [buildClientLists]);

  // Register signOut with API for 401/403 handling
  useEffect(() => {
    api.setOnUnauthorized(signOut);
  }, [signOut]);

  return (
    <AuthContext.Provider
      value={{
        authLoading,
        isAuthenticated,
        companyList,
        selectedCompanyId,
        selectedCompanyName,
        npsdClientInfoList,
        activityReportNpsdClientList,
        caseReportNpsdClientList,
        callsForServiceNpsdClientList,
        analyticsReportNpsdClientList,
        companyLogoUrl,
        isCompanyNameOnNav,
        isCompanyLogoOnNav,
        signIn,
        selectCompany,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
